import React, { useEffect, useRef } from 'react';
import { formatDateLabel } from './constants';
import useEventCategories from '../planning/useEventCategories';
import { useTranslation } from 'react-i18next';

export default function EventModal({
  open,
  mode,
  title,
  date,
  start,
  end,
  category,
  timeError,
  onChange,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation('planning');
  const { t: tCommon } = useTranslation('common');
  const titleRef = useRef(null);
  const { codes, labels } = useEventCategories();

  useEffect(() => {
    if (!open) return undefined;
    const t = window.setTimeout(() => titleRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/40 p-4 schedule-event-modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="w-full max-w-sm bg-surface p-4 rounded-xl shadow-[0_8px_24px_rgba(18,65,145,0.18)] border border-border schedule-event-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-event-heading"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-1.5 h-5 rounded-full bg-nokia-blue shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <h2 id="schedule-event-heading" className="text-sm font-bold text-primary leading-snug">
                {mode === 'edit' ? t('editEvent') : t('newEvent')}
              </h2>
              <button
                type="button"
                className="p-0.5 text-text-secondary hover:text-primary rounded transition-colors shrink-0"
                aria-label={tCommon('close')}
                onClick={onClose}
              >
                <span className="material-symbols-outlined text-[18px]">close</span>
              </button>
            </div>
            <p className="text-xs text-text-secondary mt-0.5">
              {date ? formatDateLabel(date) : ''}
            </p>
          </div>
        </div>

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit?.();
          }}
        >
          <div>
            <label htmlFor="schedule-event-title" className="block text-[11px] font-semibold text-text-secondary mb-1">
              {t('eventTitleOptional')} <span className="font-normal opacity-70">{t('optionalSuffix')}</span>
            </label>
            <input
              ref={titleRef}
              id="schedule-event-title"
              type="text"
              maxLength={80}
              placeholder={t('addTitle')}
              value={title}
              onChange={(e) => onChange?.({ title: e.target.value })}
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all"
            />
          </div>
          <div>
            <label htmlFor="schedule-event-date" className="block text-[11px] font-semibold text-text-secondary mb-1">
              Day
            </label>
            <input
              id="schedule-event-date"
              type="date"
              required
              value={date}
              onChange={(e) => onChange?.({ date: e.target.value })}
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="schedule-event-start" className="block text-[11px] font-semibold text-text-secondary mb-1">
                Start time
              </label>
              <input
                id="schedule-event-start"
                type="time"
                required
                value={start}
                onChange={(e) => onChange?.({ start: e.target.value })}
                className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all"
              />
            </div>
            <div>
              <label htmlFor="schedule-event-end" className="block text-[11px] font-semibold text-text-secondary mb-1">
                End time
              </label>
              <input
                id="schedule-event-end"
                type="time"
                required
                value={end}
                onChange={(e) => onChange?.({ end: e.target.value })}
                className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all"
              />
            </div>
          </div>
          {timeError ? (
            <p className="text-[11px] text-red-600 font-medium">{t('endTimeMustBeAfterStart')}</p>
          ) : null}
          <div>
            <span className="block text-[11px] font-semibold text-text-secondary mb-1.5">{t('category')}</span>
            <div className="category-radio-grid" role="radiogroup" aria-label={t('eventCategory')}>
              {codes.map((cat) => (
                <label key={cat}>
                  <input
                    type="radio"
                    name="schedule-event-category"
                    value={cat}
                    checked={category === cat}
                    onChange={() => onChange?.({ category: cat })}
                  />
                  <span>{labels[cat] || cat}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              className="px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-main hover:bg-background-light rounded-lg transition-colors"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 py-1.5 bg-primary hover:bg-primary-hover text-white text-xs font-medium rounded-lg active:scale-[0.98] transition-all"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
